import * as React from "react";
import { observer, inject } from "mobx-react";
import * as moment from "moment";
import * as Datetime from "react-datetime";

import { AttachmentLogEndpoints } from "src/endpoints/AttachmentLogEndpoints";
import { CustomerEndpoints } from "src/endpoints/CustomerEndpoints";

import { ConfigurationStore } from "src/stores/ConfigurationStore";
import { CustomerStore } from "src/stores/CustomerStore";
import { SessionStore } from "src/stores/SessionStore";

import { AgentAndDelegatorAuthorization, SimpleCustomer } from "src/model/Customer";
import { HorseSale } from "src/model/HorseForSale";

import { FileUploadRequestFromAttachmentFile, FileUploadRequest } from "src/requests/CommonRequest";
import { AgentAuthorizationRequest } from "src/requests/CustomerRequests";

import { CustomerLink } from "../common/CustomerLink";
import { TextareaGroup, SelectGroup } from "../common/SimpleFormComponents";
import { successMessage, errorMessage } from "../common/Toast";
import CustomerSelect from "../common/CustomerSelect";

import AgentAutorizationListComponent from "./AgentAutorizationListComponent";
import AttachmentsForm from "../common/AttachmentsForm";

namespace EditableAgentAuthorization {
    export interface Fields {
        saleId: number | null;
        agent: SimpleCustomer | null;
        dateSigned: moment.Moment | null;
        notes: string;
        attachmentLog: Array<FileUploadRequest>;
    }

    export const Empty = (): Fields => {
        return {
            agent: null,
            saleId: null,
            dateSigned: null,
            notes: "",
            attachmentLog: []
        };
    };

    export const FromModel = (m: AgentAndDelegatorAuthorization) => {
        return {
            saleId: m.sale.id,
            agent: m.agent,
            dateSigned: m.dateSigned ? moment(m.dateSigned) : null,
            notes: m.notes || "",
            attachmentLog: m.attachmentLog.files.map(f => FileUploadRequestFromAttachmentFile(f))
        };
    };

    export const ToRequest = (fields: Fields, delegator: SimpleCustomer): AgentAuthorizationRequest => {
        if (!fields.saleId || !fields.agent) {
            throw new Error("In ToRequest with an unvalidated object");
        }
        return {
            saleId: fields.saleId,
            agentId: fields.agent.id,
            delegatorId: delegator.id,
            dateSigned: fields.dateSigned ? fields.dateSigned.valueOf() : fields.dateSigned,
            notes: fields.notes,
            attachments: fields.attachmentLog
        };
    };
}

export interface Props {
    configurationStore?: ConfigurationStore;
    customerStore?: CustomerStore;
    attachmentLogEndpoints?: AttachmentLogEndpoints;
    customerEndpoints?: CustomerEndpoints;
    sessionStore?: SessionStore;
}

export interface StatePage {
    wasValidated: boolean;
    showForm: boolean;
    editingAgentAuthorizationId: number | null;
}

type State = StatePage & EditableAgentAuthorization.Fields;

const DefaultState = () => {
    return {
        ...EditableAgentAuthorization.Empty(),
        wasValidated: false,
        showForm: false,
        editingAgentAuthorizationId: null
    };
};

@inject("sessionStore")
@inject("configurationStore")
@inject("attachmentLogEndpoints")
@inject("customerStore")
@inject("customerEndpoints")
@observer
export default class AgentAutorizationComponent extends React.Component<Props, State> {
    state: State = DefaultState();

    private form: HTMLFormElement | null;

    constructor(props: Props) {
        super(props);
    }

    handleChangeDate = (date: any): void => {
        this.setState({ dateSigned: date === "" ? null : (date as moment.Moment) });
    };

    editFields = (row: AgentAndDelegatorAuthorization) => {
        this.setState({
            ...EditableAgentAuthorization.FromModel(row),
            editingAgentAuthorizationId: row.id,
            showForm: true
        });
    };

    generateRequest = (): AgentAuthorizationRequest => {
        return EditableAgentAuthorization.ToRequest(this.state, this.props.customerStore!.customer!);
    };

    createAgentAuthorization = async () => {
        try {
            const request = this.generateRequest();
            await this.props.customerStore!.createAgentAuthorization(request);
            this.clearForm();
            successMessage(`A new agent authorization has been created.`);
        } catch (error) {
            errorMessage(error);
        }
    };

    editAgentAuthorization = async () => {
        try {
            if (!this.state.editingAgentAuthorizationId) {
                throw new Error("In editAgentAuthorization but no editing ID specified?");
            }
            const request = this.generateRequest();
            await this.props.customerStore!.editAgentAuthorization(this.state.editingAgentAuthorizationId, request);
            this.clearForm();
            successMessage(`Changes successfully saved`);
        } catch (error) {
            errorMessage(error);
        }
    };

    submit = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
        event.preventDefault();
        event.stopPropagation();

        if (this.form && this.form.checkValidity() && this.state.saleId !== null && this.state.agent !== null) {
            this.setState({ wasValidated: false });
            this.state.editingAgentAuthorizationId ? this.editAgentAuthorization() : this.createAgentAuthorization();
        } else {
            this.setState({ wasValidated: true });
        }
    };

    clearForm = () => {
        this.setState(DefaultState());
    };

    render() {
        const configurationStore = this.props.configurationStore;
        if (!configurationStore) {
            return <div>No Configuration Store found.</div>;
        }

        const { customerStore } = this.props;
        if (!customerStore) {
            throw new Error(" ** No Customer Store **");
        }

        const customer = customerStore.customer;
        if (!customer) {
            throw new Error(" ** No Customer **");
        }

        const agentAuthorizations = customer.agentAuthorizations.length > 0 ? customer.agentAuthorizations : [];
        const delegatorAuthorizations = customer.delegatorAuthorizations.length > 0 ? customer.delegatorAuthorizations : [];

        return (
            <>
                <div className="card shadow">
                    <div className="card-header">Agent Authorization</div>
                    <div className="card-body">
                        <div className="row mx-0">
                            <div className="col-12 mx-0 px-0">
                                {!this.state.showForm ? (
                                    <div className="row mb-3 mx-0">
                                        <div className="col-12 mx-0 px-0 text-right">
                                            <button
                                                className={`btn btn-primary btn-sm`}
                                                onClick={e => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    this.setState({ showForm: true });
                                                }}
                                            >
                                                Create Agent Authorization
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="row mb-3  mx-0">
                                        <div className="col-12  mx-0 px-0">
                                            <div className="card mx-0 px-0 " style={{ backgroundColor: "#f7faff" }}>
                                                <div className="card-body pt-0">
                                                    <form
                                                        id="agentAuthorization"
                                                        ref={form => (this.form = form)}
                                                        className={"needs-validation mt-3" + (this.state.wasValidated ? " was-validated" : "")}
                                                        noValidate={true}
                                                    >
                                                        <div className="form-row">
                                                            <SelectGroup
                                                                label="Select Sale:"
                                                                name="documentType"
                                                                isRequired={true}
                                                                isInvalid={this.state.wasValidated && this.state.saleId === null}
                                                                className="col-md-12 col-lg-4"
                                                                disabled={false}
                                                                value={this.state.saleId || ""}
                                                                onChange={e => {
                                                                    e.preventDefault();
                                                                    const saleId = e.target.value === "" ? null : parseInt(e.target.value, 10);
                                                                    this.setState({ saleId });
                                                                }}
                                                            >
                                                                <option value="">Select sale...</option>
                                                                {configurationStore!.horseSales
                                                                    .sort((a: HorseSale, b: HorseSale) =>
                                                                        a.name.toLowerCase().localeCompare(b.name.toLowerCase())
                                                                    )
                                                                    .map((c: HorseSale, i: number) => (
                                                                        <option key={i} value={c.id}>
                                                                            {c.name}
                                                                        </option>
                                                                    ))}
                                                            </SelectGroup>
                                                            <div className="form-group col-md-12 col-lg-4">
                                                                <label htmlFor="dateOfFoaling">Date Signed:</label>
                                                                <Datetime
                                                                    dateFormat="MM/DD/YYYY"
                                                                    inputProps={{
                                                                        placeholder: "-",
                                                                        className: "form-control"
                                                                    }}
                                                                    onChange={e => this.handleChangeDate(e)}
                                                                    timeFormat={false}
                                                                    value={this.state.dateSigned || ""}
                                                                    closeOnSelect={true}
                                                                />
                                                            </div>
                                                            <div className="form-group col-md-12 col-lg-4">
                                                                <label htmlFor="files">Attach File:</label>
                                                                <AttachmentsForm
                                                                    attachments={this.state.attachmentLog}
                                                                    updateAttachmentLog={attachmentLog => this.setState({ attachmentLog })}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="form-row">
                                                            <div className="form-group col-md-12 col-lg-4">
                                                                <label htmlFor="email">
                                                                    Select Agent: <span className="text-danger">*</span>{" "}
                                                                </label>
                                                                <CustomerSelect
                                                                    customerEndpoints={this.props.customerEndpoints!}
                                                                    include="BOTH"
                                                                    showCreate={true}
                                                                    value={this.state.agent}
                                                                    onChange={agent => this.setState({ agent })}
                                                                    onCreate={agent => this.setState({ agent })}
                                                                    isInvalid={this.state.wasValidated && this.state.agent === null}
                                                                    noRedirect={true}
                                                                />
                                                                <CustomerLink
                                                                    customer={this.state.agent || null}
                                                                    className="btn btn-info btn-sm btn-block mt-2 pt-3 pb-2"
                                                                    includeAddress={true}
                                                                    includePhone={true}
                                                                    includeEmail={true}
                                                                    icons={true}
                                                                    targetBlank={true}
                                                                />
                                                            </div>
                                                            <TextareaGroup
                                                                label="Notes"
                                                                rows={6}
                                                                isRequired={false}
                                                                name="notes"
                                                                className="form-group col-md-12 col-lg-8"
                                                                value={this.state.notes}
                                                                onChange={e => this.setState({ notes: e.target.value })}
                                                            />
                                                        </div>
                                                        <div className="for-row text-right">
                                                            <button
                                                                type="button"
                                                                className="btn btn-secondary btn-sm"
                                                                onClick={e => {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    this.clearForm();
                                                                }}
                                                            >
                                                                Cancel
                                                            </button>
                                                            <button
                                                                onClick={this.submit}
                                                                form="agentAuthorization"
                                                                className="btn btn-primary btn-sm mr-0 ml-3"
                                                            >
                                                                {this.state.editingAgentAuthorizationId ? "Edit" : "Create"}
                                                            </button>
                                                        </div>
                                                    </form>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="row mx-0">
                            <div className="col-12 mx-0 px-0">
                                <AgentAutorizationListComponent title="Agent(s)" data={delegatorAuthorizations} editFields={this.editFields} />
                                <AgentAutorizationListComponent title="Agent For" data={agentAuthorizations} agentFor={true} />
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/customer/AgentAuthorization.tsx